import type { Form as ApiForm } from '@kizen/api/form';
import { createContext, useContext, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ConfirmDeletionModal from 'components/Modals/presets/ConfirmDeletion';
import useModal from '__components/Modals/useModal';
import { useToast, toastVariant } from '__components/ToastProvider';
import FormService from 'services/FormService';
import { deleteForm } from '__store/formsPage/forms.redux';

type Form = SnakeToCamelCase<ApiForm>;

type FormSettingsPageProviderProps = {
  children: ReactNode;
  form: Form;
};

type ErrorMessages = {
  name: string;
  redirectUrl: string;
};

type FormSettingsPageValue = ReturnType<typeof useFormSettings>;

const INITIAL_ERROR_MESSAGES: ErrorMessages = { name: '', redirectUrl: '' };

export const SUBMISSION_ACTIONS = {
  PAGE: 'go_to_page',
  URL: 'go_to_url',
} as const;

const FormSettingsPageContext = createContext<FormSettingsPageValue>({
  confirmDeleteModalProps: {},
  errorMessages: { name: '', redirectUrl: '' },
  form: {} as Form,
  submissionAction: undefined,
  setErrorMessages: () => {},
  setSubmissionAction: () => {},
  onExportResponses: () => Promise.resolve(),
  onShowConfirmDeleteModal: () => {},
});

const useFormSettings = (form: Form) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [showToast] = useToast();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>(
    INITIAL_ERROR_MESSAGES
  );
  const [submissionAction, setSubmissionAction] = useState<
    Form['submissionAction']
  >(form.submissionAction);

  const onExportResponses = async () => {
    try {
      await FormService.exportResponses(form);
      const message = t(
        `Responses were queued for export successfully. You will be emailed a download link once complete`
      );
      showToast({
        variant: toastVariant.SUCCESS,
        message,
      });
    } catch {
      const message = t(
        'The export was not successful. Please try again or contact Kizen support.'
      );
      showToast({
        variant: toastVariant.FAILURE,
        message,
      });
    }
  };

  const [confirmDeleteModalProps, , confirmDeleteModal] = useModal({
    handleSubmit: () => {
      dispatch(deleteForm(form?.id));
      setSubmissionAction(SUBMISSION_ACTIONS.PAGE);
      setErrorMessages(INITIAL_ERROR_MESSAGES);
      // we do not wait for the delete result from BE here so we need to redirect when the state of component is updated
      setTimeout(() => history.replace('/forms'));
    },
  });

  return {
    confirmDeleteModalProps,
    errorMessages,
    form,
    submissionAction,
    setErrorMessages,
    setSubmissionAction,
    onExportResponses,
    onShowConfirmDeleteModal: confirmDeleteModal.show,
  };
};

export const useFormSettingsPage = (): FormSettingsPageValue => {
  return useContext(FormSettingsPageContext);
};

export const FormSettingsPageProvider = (
  props: FormSettingsPageProviderProps
) => {
  const { children, form } = props;
  const { t } = useTranslation();
  const ctx = useFormSettings(form);
  const { confirmDeleteModalProps } = ctx;

  return (
    <FormSettingsPageContext.Provider value={ctx}>
      {children}
      <ConfirmDeletionModal {...confirmDeleteModalProps}>
        {t('This will permanently delete the form.')}
      </ConfirmDeletionModal>
    </FormSettingsPageContext.Provider>
  );
};
